// Decode Skysight composite-ss wind/scalar rasters.
// Formula transcribed verbatim from the captured barbs fragment shader —
// see docs/skysight-overlay-styles/wind/CAPTURE-NOTES.md (verify, don't assume).
//
// U/V are quantised indices: U in the BLUE channel, V in the ALPHA channel.
// The RED channel carries a transparent sentinel (252 = no data).

const LEVEL_COUNT = 252; // u_data_level_count
const SENTINEL = 252; // red channel value marking "no data"

// channel is a 0..1 float (e.g. pixel byte / 255). Returns 0..1 or null.
const idxToNorm = (channel) => {
  const idx = Math.round(channel * 255);
  if (idx <= 0 || idx > LEVEL_COUNT) return null;
  return (idx - 1) / (LEVEL_COUNT - 1);
};

// r,g,b,a are 0..1 channel values. min/max are [u, v] m/s ranges (uv_*: ±16).
export function decodeUV(r, g, b, a, min, max) {
  if (Math.round(r * 255) === SENTINEL) return null;
  const nu = idxToNorm(b);
  const nv = idxToNorm(a);
  if (nu === null || nv === null) return null;
  return {
    u: min[0] + nu * (max[0] - min[0]),
    v: min[1] + nv * (max[1] - min[1]),
  };
}

const MS_TO_KT = 1.9438445;

export function windSpeedDir({ u, v }) {
  const speedMs = Math.hypot(u, v);
  const knots = speedMs * MS_TO_KT;
  // Meteorological "from" direction: atan2(u, v) gives the vector heading;
  // +180 flips it to where the wind comes from. (matches the captured shader)
  const dirFrom = speedMs < 1e-4 ? 0 : (((Math.atan2(u, v) * 180) / Math.PI + 180) % 360 + 360) % 360;
  return { speedMs, knots, dirFrom };
}

// Single-channel scalar param (e.g. wblmaxmin, ridge) against its own [min,max].
// The scalar index lives in the RED channel (verified by decoding live tiles),
// which doubles as the sentinel — so unlike the uv B/A channels, idx 252 here
// means "no data", and the valid range is 1..251.
export function decodeScalar(channel, min, max) {
  const idx = Math.round(channel * 255);
  if (idx <= 0 || idx >= SENTINEL) return null;
  return min + ((idx - 1) / (LEVEL_COUNT - 1)) * (max - min);
}

export const msToKt = (ms) => ms * MS_TO_KT;
